import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import TrackCard from "../components/TrackCard";
import { BicepsFlexed, Plus } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";

export default function FavoritesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [playlists, setPlaylists] = useState([]);
  const [addingTo, setAddingTo] = useState(null);
  const { playQueue } = usePlayer();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [favs, pls] = await Promise.all([
        api.get("/favorites"),
        api.get("/playlists"),
      ]);
      setItems(favs.data || []);
      setPlaylists(pls.data || []);
    } catch (e) { setErr(formatApiError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleFav = async (trackId) => {
    await api.delete(`/favorites/${trackId}`);
    setItems(items.filter((t) => t.id !== trackId));
  };

  const addToPlaylist = async (trackId, playlistId) => {
    try {
      await api.post(`/playlists/${playlistId}/tracks`, { track_id: trackId });
      setAddingTo(null);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#C5A059] mb-2">Your saved sound</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] tracking-tight">Favorites</h1>
          <p className="text-[#A1A4AB] mt-2">{items.length} track{items.length !== 1 ? "s" : ""} saved</p>
        </div>
        {items.length > 0 && (
          <button onClick={() => playQueue(items, 0, "normal")} className="btn-primary" data-testid="favorites-play-all">
            Play all
          </button>
        )}
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="aspect-square rounded-lg shimmer" />)}</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <BicepsFlexed className="w-8 h-8 text-[#C5A059] mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-[#FDFBF7] font-serif text-2xl mb-2">No favorites yet</div>
          <div className="text-[#A1A4AB]">Tap the fist on any track to save it here.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6" data-testid="favorites-grid">
          {items.map((t, i) => (
            <div key={t.id} className="relative">
              <TrackCard
                track={t}
                list={items}
                index={i}
                showFavorite
                onFavoriteToggle={toggleFav}
                isFavorited
              />
              {playlists.length > 0 && (
                <div className="mt-2">
                  {addingTo === t.id ? (
                    <div className="card-surface p-2 space-y-1">
                      {playlists.map((p) => (
                        <button key={p.id} onClick={() => addToPlaylist(t.id, p.id)} className="w-full text-left text-xs text-[#FDFBF7] hover:bg-white/[0.03] px-2 py-1.5 rounded">
                          {p.name}
                        </button>
                      ))}
                      <button onClick={() => setAddingTo(null)} className="w-full text-xs text-[#6C707A] hover:text-[#FDFBF7] px-2 py-1.5">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setAddingTo(t.id)} className="text-xs text-[#A1A4AB] hover:text-[#C5A059] flex items-center gap-1" data-testid={`fav-add-playlist-${t.id}`}>
                      <Plus className="w-3 h-3" strokeWidth={1.5} /> Add to playlist
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
